"use client";

import React, { useEffect, useState } from "react";
import ConsolePlayer, { ConsoleVideo } from "../components/ConsolePlayer";

// Sample YouTube videos for testing the console player
const sampleVideos: ConsoleVideo[] = [
  {
    videoId: "dQw4w9WgXcQ", // Rick Roll - classic!
    title: "Rick Astley - Never Gonna Give You Up (Official Music Video)",
    channelTitle: "Rick Astley",
    thumbnailUrl: "https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg"
  },
  {
    videoId: "kJQP7kiw5Fk", // Despacito
    title: "Luis Fonsi - Despacito ft. Daddy Yankee",
    channelTitle: "Luis Fonsi",
    thumbnailUrl: "https://img.youtube.com/vi/kJQP7kiw5Fk/maxresdefault.jpg"
  },
  {
    videoId: "9bZkp7q19f0", // PSY - Gangnam Style
    title: "PSY - GANGNAM STYLE(강남스타일) M/V",
    channelTitle: "officialpsy",
    thumbnailUrl: "https://img.youtube.com/vi/9bZkp7q19f0/maxresdefault.jpg"
  },
  {
    videoId: "hTWKbfoikeg", // Nyan Cat
    title: "Nyan Cat [original]",
    channelTitle: "prguitarman",
    thumbnailUrl: "https://img.youtube.com/vi/hTWKbfoikeg/maxresdefault.jpg"
  },
  {
    videoId: "jNQXAC9IVRw", // Me at the zoo
    title: "Me at the zoo",
    channelTitle: "jawed",
    thumbnailUrl: "https://img.youtube.com/vi/jNQXAC9IVRw/maxresdefault.jpg"
  },
  {
    videoId: "ZWu5-Txt0ss", // Charlie bit my finger
    title: "Charlie bit my finger - again !",
    channelTitle: "HDCYT",
    thumbnailUrl: "https://img.youtube.com/vi/ZWu5-Txt0ss/maxresdefault.jpg"
  },
  {
    videoId: "BROWqjuTM0g", // Keyboard Cat
    title: "charlie bit my finger.avi",
    channelTitle: "charlie",
    thumbnailUrl: "https://img.youtube.com/vi/BROWqjuTM0g/maxresdefault.jpg"
  },
  {
    videoId: "dMH0bHeiRNg", // Chocolate Rain
    title: "Chocolate Rain",
    channelTitle: "TayZonday",
    thumbnailUrl: "https://img.youtube.com/vi/dMH0bHeiRNg/maxresdefault.jpg"
  }
];

type Skin = "neo2002" | "cyberdungeon" | "oled" | "crt";

export default function ConsolePlayerTest() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [skin, setSkin] = useState<Skin>("cyberdungeon");

  // Persist skin preference
  useEffect(() => {
    const saved = window.localStorage.getItem("mr_console_skin") as Skin | null;
    if (saved) setSkin(saved);
  }, []);

  useEffect(() => {
    window.localStorage.setItem("mr_console_skin", skin);
  }, [skin]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-black text-white p-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
          🎮 Winamp Console Player Test
        </h1>
        <p className="text-gray-300 text-lg">
          Test the retro gaming console player with sample YouTube videos.
          Experience the skins, controls, and playlist functionality!
        </p>
      </div>

      {/* Skin Selector */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold mb-4">🎨 Choose Console Skin</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(["neo2002", "cyberdungeon", "oled", "crt"] as Skin[]).map((skinOption) => (
              <button
                key={skinOption}
                onClick={() => setSkin(skinOption)}
                className={`px-4 py-3 rounded-lg border-2 transition-all duration-200 ${
                  skin === skinOption
                    ? "border-cyan-400 bg-cyan-400/20 text-cyan-400"
                    : "border-white/20 hover:border-white/40 text-gray-300 hover:text-white"
                }`}
              >
                <div className="font-semibold capitalize">{skinOption}</div>
                <div className="text-sm opacity-75">
                  {skinOption === "neo2002" && "Green Glow"}
                  {skinOption === "cyberdungeon" && "Orange Cyber"}
                  {skinOption === "oled" && "Blue Dark"}
                  {skinOption === "crt" && "Purple Retro"}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold mb-4">🎯 How to Test</h2>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div>
              <h3 className="font-semibold text-cyan-400 mb-2">🎮 Controls</h3>
              <ul className="space-y-1 text-gray-300">
                <li><kbd className="px-2 py-1 bg-white/10 rounded text-xs">Space</kbd> / <kbd className="px-2 py-1 bg-white/10 rounded text-xs">K</kbd> - Play/Pause</li>
                <li><kbd className="px-2 py-1 bg-white/10 rounded text-xs">↑↓←→</kbd> / <kbd className="px-2 py-1 bg-white/10 rounded text-xs">HJK</kbd> - Navigation</li>
                <li><kbd className="px-2 py-1 bg-white/10 rounded text-xs">B</kbd> - Toggle Binge Mode</li>
                <li><kbd className="px-2 py-1 bg-white/10 rounded text-xs">P</kbd> - Toggle Playlist</li>
                <li><kbd className="px-2 py-1 bg-white/10 rounded text-xs">F</kbd> - Toggle Fullscreen</li>
                <li><kbd className="px-2 py-1 bg-white/10 rounded text-xs">Esc</kbd> - Close Player</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-cyan-400 mb-2">📱 Mobile Features</h3>
              <ul className="space-y-1 text-gray-300">
                <li>• Swipe up/down to change videos</li>
                <li>• Android back button support</li>
                <li>• Touch-friendly controls</li>
                <li>• Responsive design</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Video Grid */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sampleVideos.map((video, i) => (
            <button
              key={video.videoId}
              onClick={() => {
                setIndex(i);
                setOpen(true);
              }}
              className="group text-left rounded-xl overflow-hidden bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-400/50 transition-all duration-300 hover:scale-105"
            >
              <div className="aspect-video bg-black relative overflow-hidden">
                {video.thumbnailUrl ? (
                  <img
                    src={video.thumbnailUrl}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                    <div className="text-4xl">🎵</div>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="bg-cyan-400/90 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                    <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-white line-clamp-2 mb-1 group-hover:text-cyan-400 transition-colors">
                  {video.title}
                </h3>
                {video.channelTitle && (
                  <p className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                    {video.channelTitle}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Console Player */}
      <ConsolePlayer
        open={open}
        videos={sampleVideos}
        initialIndex={index}
        skin={skin}
        onSkinChange={setSkin}
        onClose={() => setOpen(false)}
        onIndexChange={(i) => setIndex(i)}
      />

      {/* Footer */}
      <div className="max-w-4xl mx-auto mt-16 text-center">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <p className="text-gray-400 mb-2">
            Built with ❤️ using React, TypeScript, and YouTube IFrame API
          </p>
          <p className="text-sm text-gray-500">
            Experience the future of retro gaming interfaces
          </p>
        </div>
      </div>
    </div>
  );
}
